import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryPut } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { communityLinkInvalidations } from "@circle-react/lib/queries/communityLinks";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";

import type { SidebarLink } from "./LinkItem";

const EMPTY_LINKS: SidebarLink[] = []; // So that the array reference stays same across renders

const hasLinkContentChanges = (
  communityLinks: SidebarLink[],
  orderedLinks: SidebarLink[],
) => {
  if (communityLinks.length !== orderedLinks.length) return true;

  const orderedLinksById = new Map(orderedLinks.map(link => [link.id, link]));
  return communityLinks.some(link => {
    const orderedLink = orderedLinksById.get(link.id);
    return (
      link.name !== orderedLink?.name ||
      link.url !== orderedLink?.url ||
      link.may_lead_to_purchase !== orderedLink?.may_lead_to_purchase
    );
  });
};

export const useLinks = () => {
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const communityLinks = (currentCommunity?.links ??
    EMPTY_LINKS) as SidebarLink[];
  const [orderedList, setOrderedList] = useState<SidebarLink[]>(communityLinks);
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const { mutate: updateLinksOrder } = useMutation({
    mutationFn: ({ links_order_array }: { links_order_array: string[] }) =>
      reactQueryPut(internalApi.communities.communityLinks.reorder(), {
        links_order_array,
      }),
    onSuccess: () => {
      void communityLinkInvalidations.all();
    },
  });

  const onSort = (reorderedItems: SidebarLink[]) => {
    setOrderedList(reorderedItems);
    updateLinksOrder({
      links_order_array: reorderedItems.map(link => link.id.toString()),
    });
  };

  useEffect(() => {
    if (hasLinkContentChanges(communityLinks, orderedList)) {
      setOrderedList(communityLinks);
    }
  }, [communityLinks, orderedList]);

  const canSeeLinks = isAdmin || communityLinks.length > 0;

  return {
    canSeeLinks,
    isAdmin,
    onSort,
    orderedList,
  };
};
